import type { Endpoints } from '@/generated/internal-api/Endpoints';
import { errorToast, infoToast } from '@/helpers';
import { ResponseType, type ApiResponse } from '@/support/ApiService';
import { EXPORT_LIMIT_TOAST_MESSAGE, isExportLimitError } from '@/support/asyncExport';
import useMountedState from '@/support/Hooks/fetch/useMountedState';
import { useCallback } from 'react';

interface UseAsyncExportOptions {
  /**
   * Toast shown to the user once the export row is created. Defaults to a generic
   * "will email you when it's ready" message that works for both study- and team-scoped
   * exports; callers can override for domain-specific wording.
   */
  successMessage?: string;
}

/**
 * Encapsulates the post-call work for every async-export call site:
 *   - awaits the create-export API call,
 *   - guards against unmount before reading the response,
 *   - surfaces an error toast on a non-Success response,
 *   - shows the queued-export toast.
 *
 * Generation itself is driven by the SQS consumers (Java messaging-service or PHP
 * `php artisan exports:consume`) — the legacy `exports.generate` HTTP trigger has been
 * removed. Call sites supply only the create call as a thunk so endpoint typing stays
 * site-local.
 */
export const useAsyncExport = ({
  successMessage = 'Generating export. You will receive an email when complete.',
}: UseAsyncExportOptions = {}) => {
  const isMounted = useMountedState();

  return useCallback(
    async <Endpoint extends keyof Endpoints>(
      callApi: () => Promise<ApiResponse<Endpoint>>,
      // Per-call success message override — useful when one component drives multiple
      // export shapes (e.g. SamplesTable's study vs team variants).
      messageOverride?: string
    ): Promise<void> => {
      const response = await callApi();

      if (!isMounted()) {
        return;
      }

      if (response.type !== ResponseType.Success) {
        if (isExportLimitError(response.error)) {
          errorToast(EXPORT_LIMIT_TOAST_MESSAGE);
        } else {
          errorToast('Could not queue export');
        }
        return;
      }

      infoToast(messageOverride ?? successMessage);
    },
    [isMounted, successMessage]
  );
};

export default useAsyncExport;
